import { Controller, Form, FormProvider } from 'react-hook-form';
import { isNil } from 'lodash';
import { Box, Button, TextField, Typography } from '@mui/material';
import { t } from 'i18next';

import {
  CustomModal,
  FormGooglePlacesAutocomplete,
  SaveChangesModal,
} from '@components';
import { theme } from '@store';

import { useCreateBuildingModal } from './hooks';
import { USStateSelect } from 'components/USStateSelect';
import { Select } from 'ui';

export const CreateBuildingModal = ({
  isOpen,
  handleClose,
  handleOpenBuildingDetails,
}: {
  isOpen: boolean;
  handleClose: () => void;
  handleOpenBuildingDetails: (id: string) => void;
}) => {
  const {
    formMethods,
    isSubmitting,
    onSubmit,
    partnerOptions,
    organizationOptions,
    operatorOptions,
    isPartnerSelectDisabled,
    isOperatorSelectDisabled,
    setValue,
    onCloseAction,
    handleSelectLocation,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleDiscardChanges,
    handleSaveChanges,
    isValid,
    isEditSubmitting,
  } = useCreateBuildingModal(isOpen, handleClose, handleOpenBuildingDetails);

  const { control } = formMethods;

  return (
    <>
      <CustomModal
        isOpen={isOpen}
        onClose={onCloseAction}
        title={t('adminPanel.buildings.addModal.title')}
        isLoading={isSubmitting}
      >
        <FormProvider {...formMethods}>
          <Form
            control={control}
            onSubmit={e => onSubmit(e.event)}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}
          >
            <Controller
              name='name'
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  type='text'
                  label={t('adminPanel.buildings.addModal.name')}
                  variant='outlined'
                  error={!isNil(error)}
                  helperText={error ? error.message : null}
                  required
                  fullWidth
                />
              )}
            />
            {!!organizationOptions.length && (
              <Controller
                name='organization_id'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Select
                    {...field}
                    label={t('adminPanel.buildings.addModal.organization')}
                    error={!isNil(error)}
                    required
                    onChange={event => {
                      field.onChange(event);
                      setValue('partner_id', '');
                      setValue('operator_id', '');
                    }}
                    options={organizationOptions}
                    helperText={error?.message}
                  />
                )}
              />
            )}

            <Controller
              name='partner_id'
              control={control}
              render={({ field, fieldState: { error } }) => (
                <Select
                  {...field}
                  label={t('adminPanel.buildings.addModal.partner')}
                  error={!isNil(error)}
                  required
                  disabled={isPartnerSelectDisabled}
                  onChange={event => {
                    field.onChange(event);
                    setValue('operator_id', '');
                  }}
                  options={partnerOptions}
                  helperText={error?.message}
                />
              )}
            />

            <Controller
              name='operator_id'
              control={control}
              render={({ field, fieldState: { error } }) => (
                <Select
                  {...field}
                  label={t('adminPanel.buildings.addModal.operator')}
                  variant='outlined'
                  error={!isNil(error)}
                  required
                  disabled={isOperatorSelectDisabled}
                  options={operatorOptions}
                  helperText={error?.message}
                />
              )}
            />

            <Typography
              variant='subtitle2'
              sx={{
                width: '100%',
                textAlign: 'left',
              }}
            >
              {t('adminPanel.buildings.addModal.contactInformation')}
            </Typography>

            <FormGooglePlacesAutocomplete onChange={handleSelectLocation} />

            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                flexDirection: 'row',
                gap: '0.5rem',
              }}
            >
              <Controller
                name='street'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    type='text'
                    label={t('adminPanel.buildings.addModal.addressLine1')}
                    variant='outlined'
                    error={!isNil(error)}
                    helperText={error ? error.message : null}
                    required
                    fullWidth
                  />
                )}
              />
            </Box>

            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                flexDirection: 'row',
                gap: '0.5rem',
              }}
            >
              <Controller
                name='city'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    type='text'
                    label={t('adminPanel.buildings.addModal.city')}
                    variant='outlined'
                    error={!isNil(error)}
                    helperText={error ? error.message : null}
                    required
                    fullWidth
                  />
                )}
              />
              <Controller
                name='state'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <USStateSelect
                    {...field}
                    error={!!error}
                    helperText={error ? error.message : ''}
                    required
                  />
                )}
              />
            </Box>

            <Controller
              name='zip_code'
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  type='text'
                  label={t('adminPanel.buildings.addModal.zip')}
                  variant='outlined'
                  error={!isNil(error)}
                  helperText={error ? error.message : null}
                  required
                  fullWidth
                />
              )}
            />

            <Controller
              name='county'
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  type='text'
                  label={t('adminPanel.buildings.addModal.county')}
                  variant='outlined'
                  error={!isNil(error)}
                  helperText={error ? error.message : null}
                  fullWidth
                />
              )}
            />

            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                flexDirection: 'row',
                gap: '0.5rem',
              }}
            >
              <Controller
                name='phone_number'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    type='text'
                    label={t('adminPanel.buildings.addModal.phoneNumber')}
                    variant='outlined'
                    error={!isNil(error)}
                    helperText={error ? error.message : null}
                    fullWidth
                  />
                )}
              />
              <Controller
                name='fax_number'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    type='text'
                    label={t('adminPanel.buildings.addModal.faxNumber')}
                    variant='outlined'
                    error={!isNil(error)}
                    helperText={error ? error.message : null}
                    fullWidth
                  />
                )}
              />
            </Box>

            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '8px',
                position: 'sticky',
                bottom: 0,
                paddingY: 3,
                backgroundColor: theme.palette.common.white,
                zIndex: 3,
              }}
            >
              <Button
                variant='text'
                sx={{
                  fontSize: '0,875rem',
                  fontWeight: 'medium',
                  lineHeight: '24px',
                }}
                disabled={isSubmitting}
                onClick={onCloseAction}
              >
                {t('general.cancel')}
              </Button>
              <Button
                type='submit'
                variant='contained'
                color='secondary'
                sx={{
                  fontSize: '0,875rem',
                  fontWeight: 'medium',
                  lineHeight: '24px',
                  color: 'white',
                }}
                disabled={isSubmitting}
              >
                {t('general.ADD')}
              </Button>
            </Box>
          </Form>
        </FormProvider>
      </CustomModal>
      <SaveChangesModal
        isOpen={isSaveChangesModalOpen}
        onClose={handleCloseSaveChangesModal}
        isValid={isValid}
        handleSaveChanges={handleSaveChanges}
        discardChanges={handleDiscardChanges}
        isLoading={isEditSubmitting}
      />
    </>
  );
};
